/**
 * Signal-Konzept Schritt 1 (07.08.2026) — Zustand + Preis als Überschrift.
 *
 * Ersetzt die Frage „Kauf ich mit 74? Kauf ich mit 64?" (Ronni, 07.08.) durch
 * einen Satz mit einem konkreten Preis. Bewusst ein EIGENSTÄNDIGES Bauteil,
 * losgelöst von SymbolScoreCell — die Zustandsmaschine liest nur Preis und
 * Struktur (setup-state.ts) und soll nicht von den zehn Datenquellen
 * abhängen, die die Score-Engine für ihre Berechnung braucht. Fällt eine
 * Quelle aus, bleibt der Zustand trotzdem sichtbar.
 *
 * Audit 08.08.2026: Rechnet den Zustand nicht mehr selbst, sondern über
 * `useZoneState` — dieselbe Quelle, die auch der „Nur Kaufbares"-Filter
 * benutzt. Vorher liefen Anzeige (Zustandsmaschine) und Filter (Kauf-Ampel
 * aus dem Score) auseinander.
 *
 * Umbau 11.08.2026: Der Zustand kommt jetzt aus der Tagesakte. Wo das
 * ausnahmsweise nicht geht, steht es im Aufklapp-Text — eine im Browser
 * gerechnete Antwort darf nicht wie eine geprüfte aussehen.
 */
import { useEffect } from 'react';

import { HoverTooltip } from '@/components/ui/hover-tooltip';
import { useZoneState } from '@/hooks/use-zone-state';
import type { SetupState } from '@/lib/v6/setup-state';
import { cn } from '@/lib/utils';

interface ZoneStateHeadlineProps {
  symbol: string;
  assetClass?: string;
  /** Meldet den Zustand nach oben — Grundlage des „Nur Kaufbares"-Filters. */
  onZoneStateComputed?: (entryId: string, state: SetupState | null, isLoading: boolean) => void;
  entryId?: string;
}

const ZUSTAND_STYLE: Record<string, string> = {
  zone_offen: 'text-emerald-300',
  boden_reift: 'text-amber-300',
  kein_setup: 'text-zinc-500',
  ungueltig: 'text-zinc-500',
  daten_unzureichend: 'text-zinc-600',
};

export function ZoneStateHeadline({
  symbol,
  assetClass,
  onZoneStateComputed,
  entryId,
}: ZoneStateHeadlineProps) {
  const { state, isLoading, applicable, quelle, standVom } = useZoneState(symbol, assetClass);

  useEffect(() => {
    if (onZoneStateComputed && entryId) {
      onZoneStateComputed(entryId, state, isLoading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, state, isLoading]);

  if (!applicable) return null;
  if (isLoading) return <div className="mt-1 h-4 w-40 skeleton" />;
  if (!state || state.zustand === 'daten_unzureichend') return null;

  const cls = ZUSTAND_STYLE[state.zustand] ?? 'text-zinc-400';
  const tooltip = (
    <div className="space-y-1">
      <div className="font-semibold text-zinc-100">{state.text}</div>
      {state.ausloeser && (
        <div className="text-[10px] text-zinc-400">
          Auslöser: {state.ausloeser === 'wende+squeeze' ? 'Wende + Squeeze' : state.ausloeser}
        </div>
      )}
      <ul className="text-[10px] text-zinc-400 leading-snug list-disc pl-3">
        {state.gruende.slice(0, 3).map((g, i) => (
          <li key={i}>{g}</li>
        ))}
      </ul>
      <div className="text-[10px] text-zinc-500 border-t border-zinc-700/40 pt-1">
        {quelle === 'snapshot' ? (
          <>Tagesakte vom {standVom} — dieselbe Zeile, aus der Alerts entstehen.</>
        ) : (
          <>
            Im Browser gerechnet: Für diesen Wert liegt noch keine Tagesakte vor.
            Nach dem nächsten nächtlichen Lauf steht hier die geprüfte Zeile.
          </>
        )}
      </div>
      <div className="text-[10px] text-zinc-500 italic border-t border-zinc-700/40 pt-1">
        Zustand + Preis statt Punktzahl (Signal-Konzept §3/§7). Neumessung vom
        08.08.: kein nachweisbarer Vorteil gegenüber einem beliebigen
        Einstiegstag — der Vorsprung zeigt sich nur im Zeitraum, aus dem die
        Schwellen stammen. Der Zustand gibt festes Risiko und eine feste Regel,
        keine bessere Trefferquote.
      </div>
    </div>
  );

  return (
    <HoverTooltip align="left" widthRem={22} content={tooltip}>
      <div className={cn('mt-1 text-[11px] font-medium leading-snug cursor-default', cls)}>
        {state.text}
      </div>
    </HoverTooltip>
  );
}
